import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useEffect } from 'react';
import { ExternalRedirect, FeatureFlagRoute, Flex } from '@/components/common';
import { AppShell, NavBar } from '@/components/layout';
import { ReceivedLikesPanel } from '@/components/likes';
import { UserProvider } from '@/contexts';
import {
  useActivityHeartbeat,
  useAnalytics,
  useAuth,
  useOnlineStatus,
  usePwaInstallRecorder,
  useRePromptPermissions,
} from '@/hooks';
import { useConversations } from '@/hooks/useConversations';
import { useUserProfile } from '@/hooks/useUserProfile';
import { notificationService } from '@/services';
import { useFilterStore } from '@/stores/filterStore';
import {
  AccountPage,
  AuthVerification,
  BirthdayPage,
  BlockedMembersPage,
  ChangeEmailPage,
  ChatPage,
  DiscoverPage,
  EditProfilePage,
  EditSpotsPage,
  ErrorPage,
  ForgotPasswordPage,
  HelpLegalPage,
  HelpSettingsPage,
  InboxPage,
  InviteCodePage,
  LegalSettingsPage,
  LoginPage,
  MessagesTabContent,
  MyAccountSettingsPage,
  MyProfilePage,
  NotFoundPage,
  NotesPage,
  OnboardingFlow,
  OtherProfilePage,
  PaywallPage,
  PreferencesPage,
  PulpPledgePage,
  ReferralPage,
  ReportProfilePage,
  RequestDataPage,
  SettingsPage,
  SignUpPage,
  SplashScreen,
  StartPage,
  SubscriptionPage,
  YoureInPage,
} from '@/views';

const BASE_EXTERNAL_URL = 'https://pulp-app.io';
const WAITLIST_URL = 'https://tally.so/r/zxQvj0';

const EXTERNAL_REDIRECT_ROUTES = [
  <Route
    key="terms"
    path="/terms"
    element={<ExternalRedirect to={`${BASE_EXTERNAL_URL}/terms`} />}
  />,
  <Route
    key="privacy"
    path="/privacy"
    element={<ExternalRedirect to={`${BASE_EXTERNAL_URL}/privacy-policy`} />}
  />,
  <Route
    key="guidelines"
    path="/guidelines"
    element={<ExternalRedirect to={`${BASE_EXTERNAL_URL}/community-guidelines`} />}
  />,
  <Route
    key="faq"
    path="/faq"
    element={<ExternalRedirect to={`${BASE_EXTERNAL_URL}/help-center`} />}
  />,
  <Route key="waitlist" path="/waitlist" element={<ExternalRedirect to={WAITLIST_URL} />} />,
];

function App() {
  const { t } = useTranslation('chat');
  const { pathname } = useLocation();
  const showFilters = useFilterStore(s => s.showFilters);
  const showIntentions = useFilterStore(s => s.showIntentions);
  const hideNavBar =
    showFilters ||
    showIntentions ||
    pathname.startsWith('/chat') ||
    pathname.startsWith('/account/') ||
    pathname.startsWith('/profile');

  const navigate = useNavigate();

  const { user, loading: authLoading } = useAuth();
  const { profile, isLoading: profileLoading } = useUserProfile(user);
  useActivityHeartbeat(profile?.onboarded ? user : null);
  const { hasUnread } = useConversations(user);
  usePwaInstallRecorder(user);
  useAnalytics(user, profile);
  useRePromptPermissions(user);

  useEffect(() => {
    if (!user) return undefined;
    const unsubscribe = notificationService.onForegroundMessage(payload => {
      const data = payload.data || {};
      const isPhotoLike = data.type === 'photo_like';
      // Photo-like pushes are data-only; the copy is localized client-side.
      const title = isPhotoLike
        ? ((data.likerName as string) ?? (data.title as string) ?? 'Someone')
        : ((data.title as string) ?? 'New message');
      const body = isPhotoLike ? t('likes.rowCaption') : ((data.body as string) ?? '');
      const conversationId = data.conversationId as string | undefined;
      toast.info(title, {
        description: body,
        position: 'top-center',
        action:
          (isPhotoLike || conversationId) &&
          ({
            label: 'Open',
            onClick: () => navigate(isPhotoLike ? '/inbox/likes' : `/chat/${conversationId}`),
          } as const),
      });
    });
    return unsubscribe;
  }, [user, navigate, t]);

  if (authLoading) {
    return (
      <AppShell>
        <SplashScreen />
      </AppShell>
    );
  }

  const emailVerificationPending = Boolean(user?.email) && !user?.emailVerified;
  const showAuth = !user || emailVerificationPending;

  if (showAuth) {
    return (
      <AppShell>
        <Routes>
          <Route path="/" element={<StartPage />} />
          <Route path="/birthday" element={<BirthdayPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/verify-email"
            element={user ? <AuthVerification /> : <Navigate to="/" replace />}
          />
          {EXTERNAL_REDIRECT_ROUTES}
          <Route path="*" element={<Navigate to={user ? '/verify-email' : '/'} replace />} />
        </Routes>
      </AppShell>
    );
  }

  if (profileLoading) {
    return (
      <AppShell>
        <SplashScreen />
      </AppShell>
    );
  }

  if (!profile?.inviteCodeUsed) {
    return (
      <AppShell>
        <Routes>
          <Route
            path="/invite-code"
            element={user ? <InviteCodePage /> : <Navigate to="/" replace />}
          />
          {EXTERNAL_REDIRECT_ROUTES}
          <Route path="*" element={<Navigate to="/invite-code" replace />} />
        </Routes>
      </AppShell>
    );
  }

  return (
    <UserProvider user={user} currentUser={profile}>
      {profile?.onboarded === true ? (
        <AppShell>
          <Flex direction="column" flexGrow="1" minHeight="0" overflow="hidden" position="relative">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <Routes>
                {EXTERNAL_REDIRECT_ROUTES}
                <Route path="/discover" element={<DiscoverPage />} />
                <Route path="/today" element={<NotesPage />} />
                <Route path="/inbox" element={<InboxPage />}>
                  <Route index element={<MessagesTabContent />} />
                  <Route path="likes" element={<ReceivedLikesPanel />} />
                </Route>
                <Route path="/chat/:id" element={<ChatPage />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/account/my-profile" element={<MyProfilePage />} />
                <Route path="/account/my-profile/edit" element={<EditProfilePage />} />
                <Route path="/account/my-profile/edit/spots" element={<EditSpotsPage />} />
                <Route path="/account/referral" element={<ReferralPage />} />
                <Route path="/account/help-legal" element={<HelpLegalPage />} />
                <Route path="/account/settings" element={<SettingsPage />} />
                <Route path="/account/settings/preferences" element={<PreferencesPage />} />
                <Route path="/account/settings/help" element={<HelpSettingsPage />} />
                <Route path="/account/settings/about" element={<LegalSettingsPage />} />
                <Route path="/account/settings/about/request-data" element={<RequestDataPage />} />
                <Route path="/account/settings/blocked" element={<BlockedMembersPage />} />
                <Route path="/account/settings/account" element={<MyAccountSettingsPage />} />
                <Route
                  path="/account/settings/account/change-email"
                  element={<ChangeEmailPage />}
                />
                <Route path="/account/subscription" element={<SubscriptionPage />} />
                <Route
                  path="/account/subscription/paywall"
                  element={
                    <FeatureFlagRoute flag="stripe_payments" redirectTo="/account/subscription">
                      <PaywallPage />
                    </FeatureFlagRoute>
                  }
                />
                <Route path="/profile/:uid" element={<OtherProfilePage />} />
                <Route path="/profile/:uid/report" element={<ReportProfilePage />} />
                <Route path="/" element={<Navigate to="/discover" replace />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </div>
          </Flex>
          {!hideNavBar && <NavBar hasUnread={hasUnread} />}
        </AppShell>
      ) : (
        <AppShell>
          <Routes>
            <Route path="/pledge" element={<PulpPledgePage />} />
            <Route path="/youre-in" element={<YoureInPage />} />
            {EXTERNAL_REDIRECT_ROUTES}
            <Route
              path="*"
              element={
                <OnboardingFlow
                  onComplete={() => console.log('Onboarding completed successfully.')}
                />
              }
            />
          </Routes>
        </AppShell>
      )}
    </UserProvider>
  );
}

function OnlineGate() {
  const { t } = useTranslation('common');
  const isOnline = useOnlineStatus();
  if (!isOnline) {
    return (
      <ErrorPage
        title={t('offline.title')}
        message={t('offline.message', { app: t('brand.title') })}
        action={{ label: t('offline.retry'), onClick: () => window.location.reload() }}
      />
    );
  }
  return <App />;
}

export default OnlineGate;
